/* Shopify supplies the results; this component handles their presentation. */
class PPSearch extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-search-results]');
    this.status = this.querySelector('[data-search-status]');
    this.start = this.querySelector('[data-search-start]');
    this.all = this.querySelector('[data-search-all]');
    this.clear = this.querySelector('[data-search-clear]');
    this.container = this.closest('.site-header__search-container');
    this.version = 0;
    this.active = -1;
    if (this.container) {
      // Escape the fixed header's transformed and clipped stacking context.
      document.body.append(this.container);
      this.container.addEventListener('click', event => { if (!this.contains(event.target)) this.close(); });
    }
    document.addEventListener('predictive-search:open', event => {
      if (event.detail?.context !== this.dataset.context) return;
      this.opened = true;
      this.returnFocus = document.activeElement;
      this.container?.classList.add('is-active');
      document.body.classList.add('pp-search-open');
      this.input.focus();
      if (this.input.value.trim()) this.change();
    });
    this.querySelector('[data-search-close]')?.addEventListener('click', () => this.close());
    this.clear.addEventListener('click', () => { this.input.value = ''; this.change(); this.input.focus(); });
    this.input.addEventListener('input', () => this.change());
    this.addEventListener('keydown', event => this.keydown(event));
    this.querySelector('form').addEventListener('submit', event => {
      if (!this.input.value.trim()) { event.preventDefault(); this.input.focus(); }
    });
    this.clear.hidden = !this.input.value.trim();
  }
  change() {
    clearTimeout(this.timer);
    this.request?.abort();
    this.version++;
    this.active = -1;
    this.input.removeAttribute('aria-activedescendant');
    const term = this.input.value.trim();
    this.clear.hidden = !term;
    this.results.hidden = true;
    this.results.replaceChildren();
    this.all.hidden = true;
    this.start.hidden = !!term;
    this.input.setAttribute('aria-expanded', 'false');
    this.status.textContent = term ? 'Searching…' : '';
    if (term) this.timer = setTimeout(() => this.search(term, this.version), 220);
  }
  async search(term, version) {
    this.request = new AbortController();
    const params = new URLSearchParams({ q: term, 'resources[type]': 'product,collection,page,article,query', 'resources[limit]': '4', 'resources[limit_scope]': 'each', section_id: 'search-results' });
    try {
      const response = await fetch(this.dataset.url + '?' + params, { signal: this.request.signal });
      if (!response.ok) throw new Error('Search unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector('[data-search-response]');
      if (!content) throw new Error('Search response missing');
      if (version !== this.version) return;
      this.results.replaceChildren(content);
      this.options = [...this.results.querySelectorAll('[data-pp-search-option]')];
      this.options.forEach((option, index) => { option.id = 'SearchOption-' + this.dataset.context + '-' + index; });
      this.results.hidden = false;
      this.all.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      const count = Number(content.dataset.resultCount);
      this.status.textContent = count ? count + ' suggestions. Use the arrow keys to explore.' : 'No suggestions found.';
    } catch (error) {
      if (error.name === 'AbortError' || version !== this.version) return;
      this.status.textContent = 'Suggestions are unavailable. Press Search to see all results.';
      this.all.hidden = false;
    }
  }
  keydown(event) {
    if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
    if (event.target === this.input && !this.results.hidden && this.options?.length) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        this.active = this.active < 0 ? (delta > 0 ? 0 : this.options.length - 1) : (this.active + delta + this.options.length) % this.options.length;
        this.options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.active)));
        const selected = this.options[this.active];
        this.input.setAttribute('aria-activedescendant', selected.id);
        selected.scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && this.active >= 0) {
        event.preventDefault();
        this.options[this.active].click();
      }
    }
    if (event.key === 'Tab' && this.opened) {
      const items = [...this.querySelectorAll('a[href],button,input:not([type="hidden"])')].filter(el => !el.disabled && el.getClientRects().length);
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && event.target === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && event.target === last) { event.preventDefault(); first.focus(); }
    }
  }
  close() {
    clearTimeout(this.timer);
    this.request?.abort();
    this.version++;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.results.hidden = true;
    this.all.hidden = true;
    this.status.textContent = '';
    if (!this.container) return;
    this.opened = false;
    this.container.classList.remove('is-active');
    document.body.classList.remove('pp-search-open', 'predictive-overflow-hidden');
    document.dispatchEvent(new CustomEvent('predictive-search:close-all'));
    if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
  }
}
if (!customElements.get('pp-search')) customElements.define('pp-search', PPSearch);
